import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { DatePicker, Divider, Table, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import Button from '../../shared-components/buttons/Button';
import { notificationsAtom } from '../../../atoms/Atoms';
import Select from '../../shared-components/form/Select';
import api from '../../../api/api';
import SummaryCard from '../../shared-components/SummaryCard';
import { thousand } from '../../../functions';

function HmsNtd() {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [state, setState] = useState({
    loading: false,
    selectedYear: dayjs(),
    selectedGrouping: 0,
    data: null,
    showNauticalMiles: false
  });

  function getData() {
    const yearDate = dayjs(state.selectedYear);
    const yearString = yearDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    setState({
      ...state,
      loading: true
    });
    api.get(`hms/ntd?year=${yearString}&groupBy=${state?.selectedGrouping}`)
      .then((response) => {
        setState({
          ...state,
          loading: false
        });
        if (response.data.success) {
          setState({
            ...state,
            data: response.data.data
          });
        } else {
           setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setState({
          ...state,
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  useEffect(() => {
    getData();
  }, [state?.selectedYear, state?.selectedGrouping]);

  function getRouteName(route) {
    if (state?.selectedGrouping === 1) {
      return route.split(' - ')[0];
    }
    return route;
  }

  function convertMiles(miles) {
    return miles * 1.15078
  }

  function getGroupedTableColumns(isMiles) {
    return [
      {
        title: '',
        align: 'center',
        className: 'text-sm md:hidden',
        render: (row) => (
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <p>{row.label}</p>
            </div>
            <div className="text-left">
              <p><span>{t('total')}: </span>: {(isMiles ? (thousand(state?.showNauticalMiles ? row.value : convertMiles(row.value))) : thousand(row.value))}</p>
            </div>
            <div className="text-right">
              <p><span>{t('average_weekday')}: </span>: {(isMiles ? (thousand(state?.showNauticalMiles ? row.averageWeekday : convertMiles(row.averageWeekday))) : thousand(row.averageWeekday))}</p>
            </div>
            <div className="text-left">
              <p><span>{t('average_saturday')}: </span>: {(isMiles ? (thousand(state?.showNauticalMiles ? row.averageSaturday : convertMiles(row.averageSaturday))) : thousand(row.averageSaturday))}</p>
            </div>
            <div className="text-right">
              <p><span>{t('average_sunday')}: </span>: {(isMiles ? (thousand(state?.showNauticalMiles ? row.averageSunday : convertMiles(row.averageSunday))) : thousand(row.averageSunday))}</p>
            </div>
          </div>
        ),
      },
      {
        title: state?.selectedGrouping === 1 ? t('route') : t('vessel'),
        align: 'center',
        className: 'text-sm hidden md:table-cell',
        render: (row) => (
          <span>{row.label}</span>
        ),
      },
      {
        title: t('total'),
        align: 'center',
        className: 'text-sm hidden md:table-cell',
        render: (row) => (
          <span>{(isMiles ? (thousand(state?.showNauticalMiles ? row.value : convertMiles(row.value))) : thousand(row.value))}</span>
        ),
      },
      {
        title: t('average_weekday'),
        align: 'center',
        className: 'text-sm hidden md:table-cell',
        render: (row) => (
          <span>{(isMiles ? (thousand(state?.showNauticalMiles ? row.averageWeekday : convertMiles(row.averageWeekday))) : thousand(row.averageWeekday))}</span>
        ),
      },
      {
        title: t('average_saturday'),
        align: 'center',
        className: 'text-sm hidden md:table-cell',
        render: (row) => (
          <span>{(isMiles ? (thousand(state?.showNauticalMiles ? row.averageSaturday : convertMiles(row.averageSaturday))) : thousand(row.averageSaturday))}</span>
        ),
      },
      {
        title: t('average_sunday'),
        align: 'center',
        className: 'text-sm hidden md:table-cell',
        render: (row) => (
          <span>{(isMiles ? (thousand(state?.showNauticalMiles ? row.averageSunday : convertMiles(row.averageSunday))) : thousand(row.averageSunday))}</span>
        ),
      },
    ];
  }

  const directionalRouteColumns = [
    {
      title: '',
      align: 'center',
      className: 'text-sm md:hidden',
      render: (row) => (
        <div className="grid grid-cols-2">
          <div className="text-left">
            <p><span>{t('route')}: </span>: {getRouteName(row.route)}</p>
          </div>
          <div className="text-right">
            <p><span>{t('total')}: </span>: {thousand(state.showNauticalMiles ? row.total : convertMiles(row.total))}</p>
          </div>
          <div className="text-left">
            <p><span>{t('outbound')}: </span>: {thousand(state.showNauticalMiles ? row.outbound : convertMiles(row.outbound))}</p>
          </div>
          <div className="text-right">
            <p><span>{t('inbound')}: </span>: {thousand(state.showNauticalMiles ? row.inbound : convertMiles(row.inbound))}</p>
          </div>
        </div>
      ),
    },
    {
      title: t('route'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{getRouteName(row.route)}</span>
      ),
    },
    {
      title: t('outbound'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{thousand(state.showNauticalMiles ? row.outbound : convertMiles(row.outbound))}</span>
      ),
    },
    {
      title: t('inbound'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{thousand(state.showNauticalMiles ? row.inbound : convertMiles(row.inbound))}</span>
      ),
    },
    {
      title: t('total'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{thousand(state.showNauticalMiles ? row.total : convertMiles(row.total))}</span>
      ),
    },
  ];

  return (
    <MainPage>
      <H1 text="HMS - NTD" />
      <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
        <div className="col-span-1 lg:col-start-3 space-x-1">
          <span>{t('show_nautical_miles')}</span>
          <Switch
            checked={state.showNauticalMiles}
            onChange={(checked) => {
              setState({
                ...state,
                showNauticalMiles: checked
              });
            }}
          />
        </div>
        <DatePicker
          value={state?.selectedYear}
          disabled={state?.loading}
          className="h-11 bg-transparent col-span-1 lg:col-start-4"
          onChange={(obj) => {
            setState({
              ...state,
              selectedYear: obj
            });
          }}
          picker="year"
          format="YYYY"
        />
        <Select
          className="col-span-1 lg:col-start-5"
          label={t('grouping')}
          id="grouping"
          name="grouping"
          disabled={state.loading}
          value={state.selectedGrouping}
          onChange={(obj) => {
            setState({
              ...state,
              selectedGrouping: parseInt(obj.target.value, 10)
            });
          }}
          options={[
            { key: 'no_grouping', value: 0, text: t('no_grouping') },
            { key: 'route', value: 1, text: t('route') },
            { key: 'vessel', value: 2, text: t('vessel') }
          ]}
        />
        <Button
          className="bg-primaryColor px-2 col-span-1 lg:col-start-6 md:col-start-2"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={t('refresh')}
          loading={state.loading}
          onClick={() => getData()}
        />
      </div>
      <div>
        <div>
          <Divider orientation="left">{t('vehicle_revenue_hours')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <SummaryCard
              title={t('total')}
              loading={state?.loading}
              value={thousand(state?.data?.totalRevenueHours)}
            />
            <SummaryCard
              title={t('average_weekday')}
              loading={state?.loading}
              value={thousand(state?.data?.averageRevenueHoursWeekday)}
            />
            <SummaryCard
              title={t('average_saturday')}
              loading={state?.loading}
              value={thousand(state?.data?.averageRevenueHoursSaturday)}
            />
            <SummaryCard
              title={t('average_sunday')}
              loading={state?.loading}
              value={thousand(state?.data?.averageRevenueHoursSunday)}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.vehicleRevenueHours}
              columns={getGroupedTableColumns(false)}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('vehicle_revenue_miles')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <SummaryCard
              title={t('total')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.totalRevenueMiles : convertMiles(state?.data?.totalRevenueMiles))}
            />
            <SummaryCard
              title={t('average_weekday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averageRevenueMilesWeekday : convertMiles(state?.data?.averageRevenueMilesWeekday))}
            />
            <SummaryCard
              title={t('average_saturday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averageRevenueMilesSaturday : convertMiles(state?.data?.averageRevenueMilesSaturday))}
            />
            <SummaryCard
              title={t('average_sunday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averageRevenueMilesSunday : convertMiles(state?.data?.averageRevenueMilesSunday))}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.vehicleRevenueMiles}
              columns={getGroupedTableColumns(true)}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('vehicle_actual_hours')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <SummaryCard
              title={t('total')}
              loading={state?.loading}
              value={thousand(state?.data?.totalActualHours)}
            />
            <SummaryCard
              title={t('average_weekday')}
              loading={state?.loading}
              value={thousand(state?.data?.averageActualHoursWeekday)}
            />
            <SummaryCard
              title={t('average_saturday')}
              loading={state?.loading}
              value={thousand(state?.data?.averageActualHoursSaturday)}
            />
            <SummaryCard
              title={t('average_sunday')}
              loading={state?.loading}
              value={thousand(state?.data?.averageActualHoursSunday)}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.vehicleActualHours}
              columns={getGroupedTableColumns(false)}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('vehicle_actual_miles')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <SummaryCard
              title={t('total')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.totalActualMiles : convertMiles(state?.data?.totalActualMiles))}
            />
            <SummaryCard
              title={t('average_weekday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averageActualMilesWeekday : convertMiles(state?.data?.averageActualMilesWeekday))}
            />
            <SummaryCard
              title={t('average_saturday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averageActualMilesSaturday : convertMiles(state?.data?.averageActualMilesSaturday))}
            />
            <SummaryCard
              title={t('average_sunday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averageActualMilesSunday : convertMiles(state?.data?.averageActualMilesSunday))}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.vehicleActualMiles}
              columns={getGroupedTableColumns(true)}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('passenger_trips')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <SummaryCard
              title={t('total')}
              loading={state?.loading}
              value={thousand(state?.data?.totalPassengerTrips)}
            />
            <SummaryCard
              title={t('average_weekday')}
              loading={state?.loading}
              value={thousand(state?.data?.averagePassengerTripsWeekday)}
            />
            <SummaryCard
              title={t('average_saturday')}
              loading={state?.loading}
              value={thousand(state?.data?.averagePassengerTripsSaturday)}
            />
            <SummaryCard
              title={t('average_sunday')}
              loading={state?.loading}
              value={thousand(state?.data?.averagePassengerTripsSunday)}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.passengerTrips}
              columns={getGroupedTableColumns(false)}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('passenger_miles')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <SummaryCard
              title={t('total')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.totalPassengerMiles : convertMiles(state?.data?.totalPassengerMiles))}
            />
            <SummaryCard
              title={t('average_weekday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averagePassengerMilesWeekday : convertMiles(state?.data?.averagePassengerMilesWeekday))}
            />
            <SummaryCard
              title={t('average_saturday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averagePassengerMilesSaturday : convertMiles(state?.data?.averagePassengerMilesSaturday))}
            />
            <SummaryCard
              title={t('average_sunday')}
              loading={state?.loading}
              value={thousand(state.showNauticalMiles ? state?.data?.averagePassengerMilesSunday : convertMiles(state?.data?.averagePassengerMilesSunday))}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.passengerMiles}
              columns={getGroupedTableColumns(true)}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('days_operated')}</Divider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            <SummaryCard
              className="col-span-1 md:col-span-2 lg:col-span-1"
              title={t('total_operated')}
              loading={state?.loading}
              value={thousand(state?.data?.daysOperated?.totalOperated)}
            />
            <SummaryCard
              title={t('weekday')}
              loading={state?.loading}
              value={thousand(state?.data?.daysOperated?.totalWeekday)}
            />
            <SummaryCard
              title={t('saturday')}
              loading={state?.loading}
              value={thousand(state?.data?.daysOperated?.totalSaturday)}
            />
            <SummaryCard
              title={t('sunday')}
              loading={state?.loading}
              value={thousand(state?.data?.daysOperated?.totalSunday)}
            />
            <SummaryCard
              title={t('not_operated')}
              loading={state?.loading}
              value={thousand(state?.data?.daysOperated?.totalNotOperated)}
            />
          </div>
          {state?.selectedGrouping !== 0 && (
            <Table
              bordered
              loading={state?.loading}
              className="mt-1"
              dataSource={state?.data?.groupedDaysOperated}
              columns={[
                {
                  title: '',
                  align: 'center',
                  className: 'text-sm md:hidden',
                  render: (row) => (
                    <div className="grid grid-cols-2">
                      <div className="col-span-2">
                        <p>{row.label}</p>
                      </div>
                      <div className="text-left">
                        <p><span>{t('total_operated')}: </span>: {thousand(row.totalOperated)}</p>
                      </div>
                      <div className="text-right">
                        <p><span>{t('not_operated')}: </span>: {thousand(row.totalNotOperated)}</p>
                      </div>
                      <div className="text-left">
                        <p><span>{t('weekday')}: </span>: {thousand(row.totalWeekday)}</p>
                      </div>
                      <div className="text-right">
                        <p><span>{t('saturday')}: </span>: {thousand(row.totalSaturday)}</p>
                      </div>
                      <div className="text-left">
                        <p><span>{t('sunday')}: </span>: {thousand(row.totalSunday)}</p>
                      </div>
                    </div>
                  ),
                },
                {
                  title: state?.selectedGrouping === 1 ? t('route') : t('vessel'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.label}</span>
                  ),
                },
                {
                  title: t('total_operated'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.totalOperated)}</span>
                  ),
                },
                {
                  title: t('not_operated'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.totalNotOperated)}</span>
                  ),
                },
                {
                  title: t('weekday'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.totalWeekday)}</span>
                  ),
                },
                {
                  title: t('saturday'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.totalSaturday)}</span>
                  ),
                },
                {
                  title: t('sunday'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.totalSunday)}</span>
                  ),
                },
              ]}
            />
          )}
        </div>
        <div>
          <Divider orientation="left">{t('directional_route_miles')}</Divider>
          <Table
            dataSource={state?.selectedGrouping === 0 ? state?.data?.directionalMiles : state?.data?.groupedDirectionalMile}
            loading={state?.loading}
            bordered
            locale={{
              emptyText: t('no_data')
            }}
            pagination={false}
            columns={directionalRouteColumns}
          />
        </div>
      </div>
    </MainPage>
  );
}

export default HmsNtd;
